import React from 'react';
import {
  arrayOf,
  bool,
  func,
  string,
  shape,
  objectOf,
  node,
  number,
  oneOfType,
  elementType
} from 'prop-types';
import cn from 'classnames';
import { IonIcon } from '@ionic/react';

import { stringOrNumberPropTypes } from '~utils/propTypes';

import { truncate, getShowName } from './utils';
import styles from './styles.module.scss';

function Dropdown({
  id,
  label,
  placeholder,
  name,
  options,
  className,
  selected,
  currentFocus,
  isOpen,
  showOptions,
  onSelectionOption,
  onShowOptions,
  onHandledKeyDown,
  onHandleBlur,
  customChildren,
  allowUniqueOption,
  labelClassname,
  optionContainerClassName,
  maxOptionLength,
  maxLabelLength,
  labelIcon,
  error,
  isMobile,
  ariaLabel,
  inputRef
}) {
  return (
    <div
      className={cn(styles.dropdown, className, {
        [styles.isOpen]: isOpen,
        [styles.error]: error
      })}
      tabIndex="0"
      onClick={onShowOptions}
      onKeyDown={onHandledKeyDown}
      onBlur={onHandleBlur}
      id={id}>
      {label && (
        <label
          aria-label={label}
          htmlFor={name}
          className={cn('text-emperor m-bottom-2 row middle', styles.label, {
            [styles.isOpenLabel]: isOpen
          })}>
          {labelIcon && <IonIcon className={`m-right-1 ${styles.ionIcon}`} icon={labelIcon} />}
          {label}
        </label>
      )}
      <label
        aria-label={ariaLabel}
        className={cn(
          `text-select unselectable row middle full-width ${styles.dropdownLabel} ${labelClassname}`,
          {
            [styles.withArrow]: showOptions
          }
        )}>
        <span className="text-select item-1 full-width">
          {allowUniqueOption ? customChildren : selected.text}
        </span>
      </label>
      {showOptions && (
        <ul
          className={cn('column text-select m-right-3', styles.dropdownOptions, optionContainerClassName, {
            [styles.dropdownOptionsWithLabel]: label
          })}>
          {placeholder && (
            <li className={`m-bottom-1 unselectable disabled ${styles.dropdownOption}`} value="">
              {truncate(placeholder, maxLabelLength)}
            </li>
          )}
          {options.map(option => (
            <li
              key={`${name}_${option.name.toLowerCase()}`}
              className={cn('m-bottom-1 unselectable', styles.dropdownOption, {
                [styles.disabled]: option.disabled,
                [styles.currentFocus]: currentFocus === option.value
              })}
              value={option.value || option.name}
              onClick={!option.disabled && onSelectionOption}
              data-text={option.name}>
              {getShowName(option, maxOptionLength)}
            </li>
          ))}
        </ul>
      )}
      {error && <p className={styles.inputError}>{error}</p>}
      {isMobile && showOptions && (
        <select
          id={`${name}_select_mobile`}
          aria-label={ariaLabel}
          className={styles.mobileSelect}
          onClick={onSelectionOption}
          onChange={onSelectionOption}
          ref={inputRef}>
          {options.map(option => (
            <option key={`${name}_mobile_${option.name.toLowerCase()}`} value={option.value}>
              {getShowName(option, maxOptionLength)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

Dropdown.defaultProps = {
  allowUniqueOption: false,
  className: '',
  id: undefined,
  isOpen: false,
  labelClassname: '',
  optionContainerClassName: '',
  showOptions: false
};

Dropdown.propTypes = {
  isMobile: bool.isRequired,
  name: string.isRequired,
  options: arrayOf(
    shape({
      name: string.isRequired,
      value: stringOrNumberPropTypes
    })
  ).isRequired,
  selected: objectOf(stringOrNumberPropTypes).isRequired,
  onHandleBlur: func.isRequired,
  onHandledKeyDown: func.isRequired,
  onSelectionOption: func.isRequired,
  onShowOptions: func.isRequired,
  allowUniqueOption: bool,
  ariaLabel: string,
  className: string,
  currentFocus: stringOrNumberPropTypes,
  customChildren: node,
  error: string,
  id: string,
  inputRef: oneOfType([func, shape({ current: elementType })]),
  isOpen: bool,
  label: string,
  labelClassname: string,
  labelIcon: string,
  maxLabelLength: number,
  maxOptionLength: number,
  optionContainerClassName: string,
  placeholder: string,
  showOptions: bool
};

export default Dropdown;
